import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, ChevronDown, FileText, Loader2, UploadCloud, X, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);

// Uploads each file on its own request so every file gets live progress and its own success/failure.
export const useUploader = ({ headers, onDone }) => {
  const [queue, setQueue] = useState([]);
  const seq = useRef(0);
  const set = (id, patch) => setQueue((q) => q.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const add = useCallback(async (files, target) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const items = list.map((f) => ({ id: ++seq.current, file: f, name: f.name, size: f.size, pct: 0, state: 'queued', target: target.label }));
    setQueue((q) => [...items, ...q].slice(0, 40));
    let ok = 0;
    for (const it of items) {
      set(it.id, { state: 'uploading' });
      const fd = new FormData();
      fd.append('section', target.section); fd.append('parent_id', target.parent || ''); fd.append('overwrite_dupe', target.overwrite ? 1 : 0);
      fd.append('files', it.file);
      try {
        const { data } = await axios.post(`${API}/api/dropdrive/upload`, fd, { headers, onUploadProgress: (e) => e.total && set(it.id, { pct: Math.round((e.loaded / e.total) * 100) }) });
        if (data.duplicates?.length) set(it.id, { state: 'dupe', pct: 100, error: `Already here as “${data.duplicates[0].existing}”` });
        else { ok += 1; set(it.id, { state: 'done', pct: 100 }); }
      } catch (e) {
        const d = e?.response?.data?.detail;
        set(it.id, { state: 'error', error: (d && typeof d === 'object' ? d.message : d) || 'Upload failed' });
      }
    }
    onDone?.(ok);
  }, [headers, onDone]);

  const retryDupe = (it, target) => { setQueue((q) => q.filter((x) => x.id !== it.id)); add([it.file], { ...target, overwrite: true }); };
  const clear = () => setQueue((q) => q.filter((x) => x.state === 'uploading' || x.state === 'queued'));
  return { queue, add, clear, retryDupe };
};

const STATE = {
  queued: { Icon: Loader2, cls: 'text-slate-400', spin: false, label: 'Waiting' },
  uploading: { Icon: Loader2, cls: 'text-blue-600', spin: true, label: 'Uploading' },
  done: { Icon: CheckCircle2, cls: 'text-emerald-500', label: 'Saved' },
  dupe: { Icon: XCircle, cls: 'text-amber-500', label: 'Duplicate' },
  error: { Icon: XCircle, cls: 'text-rose-500', label: 'Failed' },
};

// Floating progress tray, bottom-right.
export const UploadTray = ({ queue, onClear, onUploadAnyway }) => {
  const [min, setMin] = useState(false);
  if (!queue.length) return null;
  const active = queue.filter((x) => x.state === 'uploading' || x.state === 'queued').length;
  const done = queue.filter((x) => x.state === 'done').length;
  return (
    <div className="fixed bottom-20 right-4 z-50 w-[min(380px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/15 overflow-hidden dd-pop" data-testid="dd-upload-tray">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#0f172a] text-white">
        <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/10">{active ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}</span>
        <div className="min-w-0 flex-1"><p className="text-sm font-bold" data-testid="dd-tray-title">{active ? `Uploading ${active} file${active === 1 ? '' : 's'}…` : `${done} of ${queue.length} uploaded`}</p><p className="text-[11px] text-white/60 truncate">{queue[0]?.target}</p></div>
        <button type="button" onClick={() => setMin((m) => !m)} className="p-1.5 rounded-lg hover:bg-white/10" aria-label="Minimise"><ChevronDown className={`h-4 w-4 transition-transform ${min ? 'rotate-180' : ''}`} /></button>
        {!active && <button type="button" onClick={onClear} className="p-1.5 rounded-lg hover:bg-white/10" aria-label="Close" data-testid="dd-tray-close"><X className="h-4 w-4" /></button>}
      </div>
      {!min && (
        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {queue.map((it) => { const st = STATE[it.state]; return (
            <li key={it.id} className="flex items-center gap-3 px-4 py-2.5" data-testid="dd-tray-item" data-state={it.state}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-50"><FileText className="h-4 w-4 text-slate-400" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-slate-800 truncate">{it.name}</p>
                {it.state === 'uploading' || it.state === 'queued'
                  ? <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-600 transition-[width] duration-200" style={{ width: `${it.pct}%` }} /></div>
                  : <p className={`text-[11px] ${it.error ? 'text-rose-600' : 'text-slate-400'}`}>{it.error || `${fmt(it.size)} · ${st.label}`}</p>}
                {it.state === 'dupe' && <button type="button" onClick={() => onUploadAnyway(it)} className="mt-1 text-[11px] font-bold text-blue-600 hover:underline" data-testid="dd-tray-anyway">Upload anyway</button>}
              </div>
              <st.Icon className={`h-4 w-4 shrink-0 ${st.cls} ${st.spin ? 'animate-spin' : ''}`} />
            </li>
          ); })}
        </ul>
      )}
    </div>
  );
};
